import { apiClient, type ApiEnvelope } from './client';

/**
 * Ceiling for an originate request. Production ARI answers in single-digit
 * milliseconds, so 8s is already a generous "something is wrong" line — while
 * the 30s client default is long enough that an agent gives up and re-clicks.
 */
const DIAL_TIMEOUT_MS = 8_000;

/** תג הישג של נציג, כפי שהשרת מחזיר אותו. */
export interface AgentBadge { code: string; label_he: string; label_en: string }

/**
 * מדדי הנציג עצמו — לשונית "הביצועים שלי" ומסך הסופטפון.
 * כל השדות אופציונליים: השרת עשוי להחזיר מדדים חלקיים, והמסך חייב
 * להתמודד עם זה בלי לקרוס.
 */
export interface AgentStats {
  /** deals/conversion_pct נוספו 07/09/2026 למסך "הביצועים שלי" של הנציג. */
  today?: { calls: number; answered: number; total_talk_sec: number; aht_sec: number; deals?: number; conversion_pct?: number };
  week?: { calls: number; answered: number; total_talk_sec: number; deals: number };
  week_chart?: { day: string; count: number }[];
  badges?: AgentBadge[];
}

/** שיחה אחת בתוך סבב חיוג. */
export interface BatchCall {
  call_id: number;
  lead_id: number;
  name?: string | null;
  phone: string;
  /**
   * ringing = עדיין מצלצל · answered = ענה וחובר לנציג · no_answer = נגמר
   * בלי מענה · abandoned = הלקוח ענה אבל לא חובר לאף נציג (הסופטפון לא
   * קיבל את השיחה) ונותק עם הודעת "כל הנציגים תפוסים".
   */
  state: 'ringing' | 'answered' | 'no_answer' | 'abandoned';
  status: string;
}

/** סוגי האירועים שהשרת פולט — הפרונט ממפה אותם לאייקון ולצבע. */
export type LeadEventType =
  | 'note' | 'status_changed' | 'assigned' | 'campaign_changed'
  | 'call' | 'created' | 'followup_scheduled';

export interface LeadTimelineEvent {
  id: number;
  type: LeadEventType;
  title: string;
  /** מוצג בתיבה האפורה מתחת לאירוע. */
  body?: string | null;
  meta?: Record<string, unknown> | null;
  /** מוקפא בזמן הכתיבה — נציג שעזב עדיין מוצג בשמו. */
  actor_name?: string | null;
  created_at: string;
}

export interface CallRow {
  id: number;
  direction: 'inbound' | 'outbound' | 'internal';
  status: string;
  from: string;
  to: string;
  started_at: string;
  duration_sec: number;
  talk_sec: number;
  disposition?: string;
  /** נסגרה אוטומטית בלי תיעוד (5.12) — השרת מסמן, הלקוח רק מציג. */
  undocumented?: boolean;
  wrapup?: { notes: string | null; tags: string[]; at: string | null } | null;
  has_recording?: boolean;
  other_party?: PhoneLookupResult | null;
}

export interface PhoneLookupResult {
  name: string | null;
  label: string | null;
  is_spam: boolean;
  is_verified: boolean;
  source: string;
  confidence: number;
}

export interface QuickNote {
  id: number;
  contact_id?: number;
  body: string;
  user_name: string | null;
  created_at: string | null;
}

/** הליד הפעיל של המספר בקמפיין — מוצג במסך השיחה הנכנסת (07/09/2026). */
export interface ScreenPopLead {
  id: number;
  campaign_id: number;
  campaign_name: string | null;
  campaign_status?: string | null;
  status: string;
  disposition: string | null;
  attempts: number;
  name: string | null;
  notes: string | null;
  scheduled_callback_at: string | null;
  last_attempt_at: string | null;
  assigned_agent_id: number | null;
}

export interface ScreenPop {
  phone: string;
  lookup: PhoneLookupResult | null;
  lead?: ScreenPopLead | null;
  contact: {
    id: number;
    name: string | null;
    company: string | null;
    email: string | null;
    tags: string[];
    fields: Record<string, unknown>;
    notes: { body: string; created_at: string | null }[];
  } | null;
  recent_calls: { id: number; direction: string; status: string; disposition: string | null; started_at: string | null; talk_sec: number }[];
}

export interface AiCallSummary {
  call_id: number;
  summary: string | null;
  highlights: string[];
  sentiment: 'positive' | 'neutral' | 'negative' | null;
  suggested_disposition: string | null;
}

export interface CallTranscript {
  call_id: number;
  transcript_status: 'pending' | 'queued' | 'processing' | 'done' | 'failed' | 'skipped';
  transcript: { t?: string; speaker?: string; txt?: string }[];
  summary: string | null;
  highlights: string[];
  sentiment: 'positive' | 'neutral' | 'negative' | null;
  agent_notes: string | null;
  disposition: string | null;
  started_at?: string | null;
  duration_sec?: number;
  metrics?: CallAiMetrics | null;
}

/** Semantic analysis + hard metrics computed for a transcribed call. */
export interface CallAiMetrics {
  score?: number;                     // 0-100 agent handling quality
  resolution?: 'resolved' | 'follow_up' | 'unresolved';
  sentiment_trend?: 'improved' | 'stable' | 'declined';
  topics?: string[];
  action_items?: string[];
  words_total?: number;
  wpm?: number;
  agent_talk_pct?: number;
  customer_talk_pct?: number;
}

export interface Paginated<T> {
  data: T[];
  meta: { current_page: number; last_page: number; per_page: number; total: number };
}

export interface SipCredentialsResponse {
  ws_url: string;
  sip_uri: string;
  sip_user: string;
  sip_password: string;
  sip_domain: string;
  display_name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ice_servers: any[];
  default_caller_did: string | null;
  dids: { number: string; label?: string | null }[];
}

export const agentApi = {
  async calls(direction?: string, page = 1): Promise<Paginated<CallRow>> {
    const { data } = await apiClient.get<ApiEnvelope<CallRow[]> & Paginated<CallRow>>('/agent/calls', {
      params: { direction, page },
    });
    return { data: (data.data ?? []) as CallRow[], meta: data.meta as Paginated<CallRow>['meta'] };
  },

  /** Signed, browser-playable URL for a call's recording (on demand). */
  async callRecording(callId: number): Promise<{ url: string | null; duration?: number } | null> {
    const { data } = await apiClient.get<ApiEnvelope<{ url: string | null; duration?: number }>>(`/agent/calls/${callId}/recording`);
    return data.data ?? null;
  },

  // NOTE: browser softphone calls go through JsSIP (sipStore.dial). This
  // backend ARI-originate path is reserved for click-to-call / campaign dialing.
  //
  // Timeout is deliberately far below the client default (30s): originate is a
  // fast call — ARI answers in ~2ms in production — so anything past a few
  // seconds means the request is stuck, not slow. Waiting the full 30s just
  // leaves the agent staring at a dead button with no way to retry, which is
  // exactly what "suddenly it takes ages to dial" felt like from their side.
  async dial(to: string, callerId?: string, notes?: string) {
    const { data } = await apiClient.post<ApiEnvelope<{ call_id: number; status: string }>>('/agent/calls/dial', {
      to, caller_id: callerId, notes,
    }, { timeout: DIAL_TIMEOUT_MS });
    return data;
  },

  /**
   * The agent's currently-live call id (or null). The softphone dials over
   * WebRTC and never learns its backend CDR id, so this resolves the in-progress
   * call once the CDR row lands — enabling conference/transfer/leave.
   */
  async liveCall(number?: string): Promise<{ call_id: number; status: string; direction: string } | null> {
    const { data } = await apiClient.get<ApiEnvelope<{ call_id: number; status: string; direction: string } | null>>(
      '/agent/calls/live',
      { params: number ? { number } : {} },
    );
    return data.data ?? null;
  },

  async sipCredentials(): Promise<SipCredentialsResponse | null> {
    const { data } = await apiClient.get<ApiEnvelope<SipCredentialsResponse>>('/agent/sip-credentials');
    return data.data ?? null;
  },

  async callDispositions(campaignId?: number) {
    const { data } = await apiClient.get<ApiEnvelope<{ code: string; label: string; color?: string; requires_followup?: boolean; requires_note?: boolean; alert_before_followup?: boolean; lead_status?: string | null; reasons?: Array<{ code: string; label: string }> | null }[]>>('/agent/calls/dispositions', { params: campaignId != null ? { campaign_id: campaignId } : undefined });
    return data.data ?? [];
  },

  /**
   * ניתוק חירום של השיחה הפעילה, בצד השרת.
   * עובד גם כשהסופטפון אינו מחזיק את השיחה (שיחת תותח, טאב שנפתח מחדש).
   */
  async dialerHangup() {
    const { data } = await apiClient.post<ApiEnvelope<{ hung_up: boolean }>>('/agent/dialer/hangup');
    return data.data?.hung_up ?? false;
  },

  /** מצב סבב חיוג: מי חויג, מי ענה, ומי לא. */
  async dialerBatchStatus(campaignId: number, batch: string) {
    const { data } = await apiClient.get<ApiEnvelope<{ calls: BatchCall[] }>>(
      `/agent/dialer/campaigns/${campaignId}/batch-status`, { params: { batch } },
    );
    return data.data?.calls ?? [];
  },

  /** אירוע יחיד בציר הזמן של הליד. */
  async leadTimeline(leadId: number) {
    const { data } = await apiClient.get<ApiEnvelope<LeadTimelineEvent[]>>(`/agent/leads/${leadId}/timeline`);
    return data.data ?? [];
  },

  /**
   * הוספת/עדכון שדה מותאם אישית על הליד. מחזיר את מפת השדות המעודכנת
   * מהשרת — לא ממזגים בצד הלקוח, כדי שהתצוגה תשקף את מה שנשמר בפועל.
   */
  async addLeadField(leadId: number, key: string, value: string) {
    const { data } = await apiClient.post<ApiEnvelope<Record<string, unknown>>>(
      `/agent/leads/${leadId}/fields`, { key, value },
    );
    return data.data ?? {};
  },

  /** שורת הקלט בתחתית ציר הזמן — "הוספת הערה…". */
  async addLeadNote(leadId: number, body: string) {
    await apiClient.post(`/agent/leads/${leadId}/notes`, { body });
  },

  /**
   * המוצרים הזמינים בקמפיין, לבחירה בסגירת עסקה (אפיון 6.9).
   * מחזיר שם ומזהה בלבד — **המחיר לא נחשף לנציג**.
   */
  async agentProducts(campaignId: number) {
    const { data } = await apiClient.get<ApiEnvelope<{ id: number; name: string }[]>>('/agent/products', { params: { campaign_id: campaignId } });
    return data.data ?? [];
  },

  /** Admin-defined call tags available during wrap-up. */
  async callTags() {
    const { data } = await apiClient.get<ApiEnvelope<{ slug: string; label: string; color?: string }[]>>('/agent/calls/tags');
    return data.data ?? [];
  },

  async disposeCall(callId: number, payload: { disposition: string; notes?: string; callback_at?: string | null; tags?: string[]; reason_code?: string }) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/agent/calls/${callId}/dispose`, payload);
    return data.data;
  },

  /**
   * Wrap-up when the client doesn't yet have the Call row id (softphone/JsSIP
   * calls — their CDR row is created server-side on hangup). The backend
   * resolves the agent's most recent call to this number and disposes it.
   */
  async disposeLatestCall(payload: { number: string; disposition: string; notes?: string; callback_at?: string | null; tags?: string[]; reason_code?: string }) {
    // The CDR webhook may not have created the Call row yet (hangup → webhook
    // race). Retry a few times on 404 before giving up.
    let lastErr: unknown;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const { data } = await apiClient.post<ApiEnvelope<unknown>>('/agent/calls/dispose-latest', payload);
        return data.data;
      } catch (e) {
        lastErr = e;
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status !== 404) throw e;
        await new Promise((r) => setTimeout(r, 1500));
      }
    }
    throw lastErr;
  },

  /**
   * AI wrap-up assist — transcribe + summarize the agent's most recent call to
   * a number. Returns a summary, highlights, sentiment, and a suggested
   * disposition code the agent can accept or override.
   */
  async aiCallSummary(number: string): Promise<AiCallSummary> {
    const { data } = await apiClient.post<ApiEnvelope<AiCallSummary>>('/agent/calls/ai-summary', { number });
    return data.data as AiCallSummary;
  },

  async contacts(q?: string, page = 1): Promise<Paginated<unknown>> {
    const { data } = await apiClient.get('/agent/contacts', { params: { q, page } });
    return data as Paginated<unknown>;
  },

  async getContact(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>(`/agent/contacts/${id}`);
    return data.data;
  },

  /** Call history (matched by phone) for a contact, each with has_recording. */
  async contactCalls(id: number): Promise<CallRow[]> {
    const { data } = await apiClient.get<ApiEnvelope<CallRow[]>>(`/agent/contacts/${id}/calls`);
    return (data.data ?? []) as CallRow[];
  },

  async lookup(phone: string): Promise<PhoneLookupResult> {
    const { data } = await apiClient.get<ApiEnvelope<PhoneLookupResult>>('/agent/phone-lookup', { params: { phone } });
    return data.data!;
  },

  /** Chat-style quick notes for a number — each note is its own row. */
  async quickNotes(phone: string): Promise<{ contact_id: number | null; notes: QuickNote[] }> {
    const { data } = await apiClient.get<ApiEnvelope<{ contact_id: number | null; notes: QuickNote[] }>>('/agent/quick-notes', { params: { phone } });
    return data.data ?? { contact_id: null, notes: [] };
  },

  async addQuickNote(phone: string, body: string): Promise<QuickNote> {
    const { data } = await apiClient.post<ApiEnvelope<QuickNote>>('/agent/quick-notes', { phone, body });
    return data.data!;
  },

  /** Full CRM context for an inbound caller — for the incoming-call popup. */
  async screenPop(phone: string): Promise<ScreenPop | null> {
    const { data } = await apiClient.get<ApiEnvelope<ScreenPop>>('/agent/screen-pop', { params: { phone } });
    return data.data ?? null;
  },

  /** Read-only transcript + AI summary of the agent's latest call to a number (poll). */
  async transcriptLatest(number: string): Promise<CallTranscript | null> {
    const { data } = await apiClient.get<ApiEnvelope<CallTranscript>>('/agent/calls/transcript-latest', { params: { number } });
    return data.data ?? null;
  },

  /** Transcript + AI summary of a specific past call (History, customer history). */
  async callTranscript(callId: number): Promise<CallTranscript | null> {
    const { data } = await apiClient.get<ApiEnvelope<CallTranscript>>(`/agent/calls/${callId}/transcript`);
    return data.data ?? null;
  },

  /** Create a CRM contact (auto-save of a new customer card). Idempotent by phone. */
  async createContact(payload: { phone: string; name?: string; email?: string; fields?: Record<string, unknown> }) {
    const { data } = await apiClient.post<ApiEnvelope<{ id: number }>>('/agent/contacts', payload);
    return data.data;
  },

  /** Update an existing contact card. */
  async updateContact(id: number, patch: { name?: string; email?: string; fields?: Record<string, unknown> }) {
    const { data } = await apiClient.patch<ApiEnvelope<{ id: number }>>(`/agent/contacts/${id}`, patch);
    return data.data;
  },

  // ── לידים של הנציג (לשוניות "בטיפולי" ו"חיפוש") ──────────────────
  //
  // שתי הנקודות האלה עדיין לא קיימות בשרת (origin/main מכיר רק
  // POST dialer/campaigns/{id}/next שמושך ליד בודד מהתור, בלי רשימה
  // ובלי חיפוש). הן נקראות כאן בשמן הצפוי, ומחזירות רשימה ריקה אם
  // השרת מחזיר 404 — כך המסך מציג "אין לידים" במקום ליפול.

  /** הלידים הפתוחים המשויכים לנציג, לסינון לפי קמפיין וסטטוס. */
  async myLeads(params?: { campaign_id?: number; status?: string }): Promise<AgentLead[]> {
    try {
      const { data } = await apiClient.get<ApiEnvelope<AgentLead[]>>('/agent/leads', { params });
      return (data.data ?? []) as AgentLead[];
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) return [];
      throw e;
    }
  },

  /**
   * חיפוש לידים לפי טלפון או שם, בקמפיינים של הנציג בלבד. השרת אמור
   * להחזיר תוצאה אחת לאדם; הדדופ בצד הלקוח (leadsByPerson) הוא רשת
   * ביטחון לתשובה שמחזירה שורה לכל קמפיין.
   */
  async searchLeads(q: string): Promise<AgentLead[]> {
    try {
      const { data } = await apiClient.get<ApiEnvelope<AgentLead[]>>('/agent/leads/search', { params: { q } });
      return (data.data ?? []) as AgentLead[];
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) return [];
      throw e;
    }
  },

  async callbacks() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/agent/callbacks');
    return data.data ?? [];
  },

  async createCallback(payload: { phone: string; scheduled_at: string; reason?: string; source_call_id?: number; name?: string }) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/agent/callbacks', payload);
    return data.data;
  },

  async updateCallback(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/agent/callbacks/${id}`, patch);
    return data.data;
  },

  async deleteCallback(id: number) {
    await apiClient.delete(`/agent/callbacks/${id}`);
  },

  /** Ask a supervisor to reassign one of my callbacks to another agent. */
  async proposeCallbackTransfer(id: number, userId: number, note?: string) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/agent/callbacks/${id}/propose-transfer`, { user_id: userId, note });
    return data.data;
  },

  async tasks() {
    const { data } = await apiClient.get<ApiEnvelope<{ today: unknown[]; upcoming: unknown[]; done: unknown[] }>>('/agent/tasks');
    return data.data!;
  },

  async createTask(payload: { title: string; description?: string; due_at?: string }) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/agent/tasks', payload);
    return data.data;
  },

  async updateTask(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/agent/tasks/${id}`, patch);
    return data.data;
  },

  async deleteTask(id: number) {
    await apiClient.delete(`/agent/tasks/${id}`);
  },

  async voicemails() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/agent/voicemails');
    return data.data ?? [];
  },

  async voicemailSettings(): Promise<{ voicemail_enabled: boolean }> {
    const { data } = await apiClient.get<ApiEnvelope<{ voicemail_enabled: boolean }>>('/agent/voicemails/settings');
    return data.data ?? { voicemail_enabled: true };
  },

  async setVoicemailEnabled(enabled: boolean): Promise<{ voicemail_enabled: boolean }> {
    const { data } = await apiClient.post<ApiEnvelope<{ voicemail_enabled: boolean }>>('/agent/voicemails/settings', { voicemail_enabled: enabled });
    return data.data ?? { voicemail_enabled: enabled };
  },

  async markVoicemailRead(id: number) {
    await apiClient.post(`/agent/voicemails/${id}/read`);
  },

  async deleteVoicemail(id: number) {
    await apiClient.delete(`/agent/voicemails/${id}`);
  },

  async notifications() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]> & { meta: { unread: number } }>('/agent/notifications');
    return data;
  },

  async markNotificationRead(id: number) {
    await apiClient.post(`/agent/notifications/${id}/read`);
  },

  async markAllNotificationsRead() {
    await apiClient.post('/agent/notifications/read-all');
  },

  async stats() {
    const { data } = await apiClient.get<ApiEnvelope<AgentStats>>('/agent/stats');
    return data.data ?? null;
  },

  /**
   * רשימת עמיתי הדייר (לבחירת נציג יעד בהעברת שיחה חוזרת). היה חלק
   * מ-API הצ'אט; הצ'אט הוסר אבל הרשימה עצמה אינה קשורה אליו.
   */
  async colleagues() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/agent/colleagues');
    return data.data ?? [];
  },

  async shifts(weekStart?: string) {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/agent/shifts', { params: { week_start: weekStart } });
    return data.data ?? [];
  },

  async requestShiftSwap(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/agent/shifts/${id}/swap`);
    return data.data;
  },

  async scripts() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/agent/scripts');
    return data.data ?? [];
  },

  async settings() {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/agent/settings');
    return data.data;
  },

  async setPresence(status: string) {
    await apiClient.post('/agent/settings/presence', { status });
  },

  async dialerCampaigns() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/agent/dialer/campaigns');
    return data.data ?? [];
  },

  /** מצב התותח עבור הנציג — מזין את ה-DialerBar הקבוע. */
  async dialerState(): Promise<DialerState> {
    const { data } = await apiClient.get<ApiEnvelope<DialerState>>('/agent/dialer/state');
    return data.data!;
  },

  /** השהיה/חידוש חיוג אוטומטי עבור הנציג בקמפיין (DialerBar). */
  /**
   * השהיית/חידוש החיוג האוטומטי לנציג. בעצירה נדרשת סיבה (אפיון 5.3) —
   * השרת מחזיר 422 בלעדיה, ולכן היא פרמטר חובה בפועל כש-paused=true.
   */
  async dialerPause(campaignId: number, paused: boolean, reason?: string) {
    const { data } = await apiClient.post<ApiEnvelope<{ campaign_id: number; paused: boolean }>>(
      `/agent/dialer/campaigns/${campaignId}/pause`,
      paused ? { paused, reason } : { paused },
    );
    return data.data!;
  },

  /**
   * "עצור" בתותח: משהה את הנציג בקמפיין **ומנתק את כל שיחות הסבב** שעדיין
   * מצלצלות. עצירת הלולאה בדפדפן לבדה לא עצרה אותן, והלקוח שענה קיבל
   * שיחה שאיש כבר לא חיכה לה.
   */
  async dialerStopRounds(campaignId: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ campaign_id: number; hung_up: number; paused: boolean }>>(
      `/agent/dialer/campaigns/${campaignId}/stop-rounds`,
      {},
    );
    return data.data!;
  },

  /** סיבות העצירה שהשרת מכיר (הפסקה / צהריים / סוף יום). */
  async dialerPauseReasons(): Promise<Array<{ code: string; label: string }>> {
    const { data } = await apiClient.get<ApiEnvelope<Array<{ code: string; label: string }>>>('/agent/dialer/pause-reasons');
    return data.data ?? [];
  },

  async dialerNext(campaignId: number) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/agent/dialer/campaigns/${campaignId}/next`);
    return data.data;
  },

  /** Fire the campaign's parallel lines at once; first answer wins, rest drop. */
  async dialerDialBatch(campaignId: number, opts?: { forceWindow?: boolean }) {
    const { data } = await apiClient.post<ApiEnvelope<{
      batch: string; lines: number; dialed: number;
      /** כל הלידים שהסבב חייג אליהם — לא רק הראשון. */
      leads?: Array<{ id: number; phone: string; name?: string | null }>;
      /** מחוץ לשעות הקמפיין — הודעה בלבד, החיוג יצא. */
      outside_window?: boolean;
      window_notice?: string | null;
    }>>(
      `/agent/dialer/campaigns/${campaignId}/dial-batch`,
      opts?.forceWindow ? { force_window: true } : {},
    );
    return data.data;
  },

  async dialerProgress(campaignId: number) {
    const { data } = await apiClient.get<ApiEnvelope<{ total: number; remaining: number; done: number; by_status: Record<string, number> }>>(`/agent/dialer/campaigns/${campaignId}/progress`);
    return data.data;
  },

  /**
   * חמשת הראשונים מיקומיים מטעמי תאימות; שדות חדשים נכנסים ב-`extra` כדי
   * שלא תיווצר שרשרת ארגומנטים אופציונליים שקשה לקרוא ולנחש.
   */
  async dialerDispose(
    leadId: number,
    disposition: string,
    callbackAt?: string,
    nextStatus?: string,
    notes?: string,
    extra?: { product_id?: number | null; reason_code?: string | null; callback_phone?: string | null; callback_name?: string | null },
  ) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/agent/dialer/leads/${leadId}/dispose`, {
      disposition, callback_at: callbackAt, next_status: nextStatus, notes,
      ...(extra?.product_id != null ? { product_id: extra.product_id } : {}),
      ...(extra?.reason_code ? { reason_code: extra.reason_code } : {}),
      // "לחזור למספר אחר": המספר והשם של מי שחוזרים אליו.
      ...(extra?.callback_phone ? { callback_phone: extra.callback_phone } : {}),
      ...(extra?.callback_name ? { callback_name: extra.callback_name } : {}),
    });
    return data.data;
  },

  /** Run a campaign quick-action of type=automation for the current call context. */
  async runQuickAction(payload: { automation_id: number; campaign_id: number; lead_id?: number | null; call_id?: string | null; phone?: string | null; name?: string | null }) {
    const { data } = await apiClient.post<ApiEnvelope<{ ok?: boolean; message?: string }>>('/agent/dialer/quick-actions/run', payload);
    return data.data;
  },

  // Live call control (transfer / conference / leave) for a bridged campaign call.
  async transferTargets(): Promise<TransferTarget[]> {
    const { data } = await apiClient.get<ApiEnvelope<TransferTarget[]>>('/agent/calls/transfer-targets');
    return data.data ?? [];
  },
  async transferCall(callId: number, userId: number, mode: 'blind' | 'attended') {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/agent/calls/${callId}/transfer`, { user_id: userId, mode });
    return data.data;
  },
  async conferenceCall(callId: number, userId: number) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/agent/calls/${callId}/conference`, { user_id: userId });
    return data.data;
  },
  /** Add an external phone number into the conference (an extra customer/party). */
  async conferenceExternal(callId: number, number: string) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/agent/calls/${callId}/conference`, { number });
    return data.data;
  },
  /** Live conference roster — everyone currently on the call. */
  async callParticipants(callId: number): Promise<ConferenceParticipant[]> {
    const { data } = await apiClient.get<ApiEnvelope<ConferenceParticipant[]>>(`/agent/calls/${callId}/participants`);
    return data.data ?? [];
  },
  async leaveCall(callId: number) {
    await apiClient.post(`/agent/calls/${callId}/leave`);
  },

  // ── מועדפי חיוג ──────────────────────────────────────────────────
  async favorites(): Promise<Favorite[]> {
    const { data } = await apiClient.get<ApiEnvelope<Favorite[]>>('/agent/favorites');
    return data.data ?? [];
  },
  async addFavorite(number: string, name?: string): Promise<Favorite> {
    const { data } = await apiClient.post<ApiEnvelope<Favorite>>('/agent/favorites', { number, ...(name ? { name } : {}) });
    return data.data!;
  },
  async removeFavorite(id: number) {
    await apiClient.delete(`/agent/favorites/${id}`);
  },
};

export interface Favorite {
  id: number;
  number: string;
  display?: string | null;
  name?: string | null;
}

// ── Live call control ──────────────────────────────────────────────
export interface DialerStateCampaign {
  id: number;
  name: string;
  dialer_mode: string;
  auto: boolean;
  paused: boolean;
  in_window: boolean;
  /** מונים לכרטיס הקמפיין במסך "הקמפיינים שלי" (08/09/2026). */
  leads_total?: number;
  leads_queued?: number;
  leads_mine?: number;
}
export interface DialerState {
  campaigns: DialerStateCampaign[];
  active_call: {
    id: number;
    campaign_id: number;
    to_number: string;
    lead_name?: string | null;
    status: string;
    started_at?: string | null;
  } | null;
}

export interface TransferTarget {
  id: number;
  name: string;
  role: string;
  supervisor_id?: number | null;
  presence?: string;
}

/** A single party on a conference call (customer / agent / external number). */
export interface ConferenceParticipant {
  kind: 'customer' | 'agent' | 'external';
  id: number | string;
  label: string;
  detail: string | null;
}

// ── Campaign quick actions (during a call) ─────────────────────────
export interface AgentQuickAction {
  label: string;
  icon?: string;
  type: 'url' | 'automation';
  url?: string;
  automation_id?: number;
}

/**
 * ליד קמפיין כפי שהנציג רואה אותו. `contact_id` הוא מזהה האדם — ליד הוא
 * הכניסה של איש קשר לקמפיין אחד, ולכן אותו אדם יכול להופיע בכמה לידים.
 */
export interface AgentLead {
  id: number;
  contact_id?: number | null;
  campaign_id: number;
  campaign_name?: string | null;
  phone: string;
  name?: string | null;
  status: string;
  disposition?: string | null;
  attempts?: number;
  last_attempt_at?: string | null;
  scheduled_callback_at?: string | null;
}
